import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import DesktopLayout from '../layouts/DesktopLayout';
import serviceApi from '../services/serviceApi';
import { formatDate } from '../utils/dateFormatter';
import {
  CLIENT_TYPE_LABELS,
  LOCATION_TYPE_LABELS,
  REQUEST_STATUS_LABELS,
  PAYMENT_STATUS_LABELS,
  SUBSCRIPTION_STATUS_LABELS,
} from '../config/serviceConstants';
import CreateLocationModal from '../components/CreateLocationModal';
import CreateSubscriptionModal from '../components/CreateSubscriptionModal';
import EditSubscriptionModal from '../components/EditSubscriptionModal';
import RecordPaymentModal from '../components/RecordPaymentModal';
import EditClientModal from '../components/EditClientModal';
import '../styles/ServiceClientDetail.css';

const ServiceClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeNavItem, setActiveNavItem] = useState('services');
  const [copied, setCopied] = useState(false);

  const [client, setClient] = useState(null);
  const [locations, setLocations] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [paymentDefaults, setPaymentDefaults] = useState(null);

  // Modal state
  const [showEditClient, setShowEditClient] = useState(false);
  const [showCreateLoc, setShowCreateLoc] = useState(false);
  const [showCreateSub, setShowCreateSub] = useState(false);
  const [showEditSub, setShowEditSub] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [requests, setRequests] = useState([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await serviceApi.getClient(id);
      const data = res.data || {};

      setClient(data.client);
      setLocations(data.locations || []);
      setRequests(data.requests || []);

      // Fetch subscriptions separately for balance computation
      const subsRes = await serviceApi.getClientSubscriptions(id);
      setSubscriptions(subsRes.data?.subscriptions || []);
      // Server-provided form default (Riyadh business date) — see InvoiceDetailPage.
      setPaymentDefaults(subsRes.data?.defaults || null);
    } catch (err) {
      console.error('Error fetching client detail:', err);
      setError('تعذر تحميل بيانات العميل');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopyLink = () => {
    const token = client?.userId?.clientAccessToken;
    if (!token) return;
    const url = `${window.location.origin}/#/client/t/${token}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleRegenerateToken = async () => {
    if (!window.confirm('سيتم إلغاء الرابط الحالي وإنشاء رابط جديد — هل أنت متأكد؟')) return;
    try {
      await serviceApi.regenerateToken(id);
      await fetchData();
    } catch (err) {
      console.error('Error regenerating token:', err);
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('هل أنت متأكد من إلغاء هذا الطلب؟')) return;
    try {
      await serviceApi.cancelRequest(requestId);
      await fetchData();
    } catch (err) {
      console.error('Error cancelling request:', err);
    }
  };

  const getBalanceFillClass = (balance, total) => {
    if (total === 0) return 'svc-balance-bar__fill--zero';
    const pct = balance / total;
    if (pct <= 0) return 'svc-balance-bar__fill--zero';
    if (pct <= 0.25) return 'svc-balance-bar__fill--low';
    return 'svc-balance-bar__fill--ok';
  };

  if (loading) {
    return (
      <DesktopLayout activeNavItem={activeNavItem} setActiveNavItem={setActiveNavItem}>
        <div className="svc-detail">
          <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">جاري التحميل...</span>
            </div>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  if (error || !client) {
    return (
      <DesktopLayout activeNavItem={activeNavItem} setActiveNavItem={setActiveNavItem}>
        <div className="svc-detail">
          <button className="svc-detail__back" onClick={() => navigate('/services/clients')}>
            <i className="bi bi-arrow-right" />
            العودة للقائمة
          </button>
          <div style={{ textAlign: 'center', padding: '48px 16px', color: '#6c757d' }}>
            <i className="bi bi-exclamation-triangle" style={{ fontSize: '2rem', color: '#dc3545' }} />
            <p style={{ marginTop: 12 }}>{error || 'العميل غير موجود'}</p>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  const dashboardToken = client.userId?.clientAccessToken;

  return (
    <DesktopLayout
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
      headerTitle="تفاصيل العميل"
    >
      <div className="svc-detail">
        {/* Back */}
        <button className="svc-detail__back" onClick={() => navigate('/services/clients')}>
          <i className="bi bi-arrow-right" />
          العودة للقائمة
        </button>

        {/* Profile */}
        <div className="svc-detail__profile">
          <div className="svc-detail__profile-info">
            <h3>
              {client.userId?.name || '—'}
              <span className={`svc-status-dot svc-status-dot--${client.status}`} />
            </h3>
            <div className="svc-detail__profile-meta">
              {client.userId?.clientCode && (
                <span><i className="bi bi-hash" /> {client.userId.clientCode}</span>
              )}
              <span><i className="bi bi-envelope" /> {client.userId?.email || '—'}</span>
              {client.userId?.phone && (
                <span><i className="bi bi-telephone" /> <span dir="ltr">{client.userId.phone}</span></span>
              )}
              {client.userId?.nationalId && (
                <span><i className="bi bi-person-badge" /> {client.userId.nationalId}</span>
              )}
              <span>
                <i className="bi bi-tag" />
                <span className={`svc-type-badge svc-type-badge--${client.type}`}>
                  {CLIENT_TYPE_LABELS[client.type] || client.type}
                </span>
              </span>
              {client.companyName && (
                <span><i className="bi bi-building" /> {client.companyName}</span>
              )}
            </div>
          </div>
          <div className="svc-detail__profile-actions">
            <button
              className="svc-action svc-action--primary"
              onClick={() => navigate(`/services/requests/new?clientId=${id}`)}
            >
              <i className="bi bi-plus-circle" />
              طلب تنظيف
            </button>
            <button
              className="svc-action svc-action--outline"
              onClick={() => setShowEditClient(true)}
            >
              <i className="bi bi-pencil" />
              تعديل
            </button>
            <button
              className="svc-action svc-action--outline"
              onClick={handleRegenerateToken}
            >
              <i className="bi bi-arrow-clockwise" />
              تجديد الرابط
            </button>
          </div>
        </div>

        {/* Dashboard link */}
        {dashboardToken && (
          <div className="svc-detail__dash-link">
            <div className="svc-detail__dash-url">
              {window.location.origin}/#/client/t/{dashboardToken}
            </div>
            <button
              className={`svc-detail__copy-btn ${copied ? 'svc-detail__copy-btn--copied' : ''}`}
              onClick={handleCopyLink}
            >
              <i className={`bi ${copied ? 'bi-check-lg' : 'bi-clipboard'}`} />
              {copied ? 'تم النسخ' : 'نسخ رابط الواتساب'}
            </button>
          </div>
        )}

        {/* Locations */}
        <div className="svc-detail__section">
          <div className="svc-detail__section-header">
            <h4 className="svc-detail__section-title">
              <i className="bi bi-geo-alt" style={{ marginInlineStart: 6 }} />
              المواقع ({locations.length})
            </h4>
            <button
              className="svc-action svc-action--outline"
              style={{ padding: '6px 14px', fontSize: '0.82rem' }}
              onClick={() => setShowCreateLoc(true)}
            >
              <i className="bi bi-plus" />
              إضافة موقع
            </button>
          </div>

          {locations.length === 0 ? (
            <p style={{ color: '#6c757d', fontSize: '0.9rem' }}>لا توجد مواقع بعد</p>
          ) : (
            <div className="svc-detail__locations">
              {locations.map(loc => (
                <div key={loc._id} className="svc-loc-card">
                  <div className="svc-loc-card__header">
                    <span className="svc-loc-card__name">{loc.name}</span>
                    <span className="svc-loc-card__code">{loc.locationCode}</span>
                  </div>
                  <div className="svc-loc-card__meta">
                    <span>
                      <i className="bi bi-house" style={{ marginInlineStart: 4 }} />
                      {LOCATION_TYPE_LABELS[loc.type] || loc.type}
                    </span>
                    {loc.address?.city && (
                      <span>
                        <i className="bi bi-pin-map" style={{ marginInlineStart: 4 }} />
                        {[loc.address.district, loc.address.city].filter(Boolean).join('، ')}
                      </span>
                    )}
                    {loc.size?.bedrooms !== undefined && (
                      <span>
                        {loc.size.bedrooms} غرف • {loc.size.bathrooms || 0} حمام
                        {loc.size.sqm ? ` • ${loc.size.sqm} م²` : ''}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Subscriptions */}
        <div className="svc-detail__section">
          <div className="svc-detail__section-header">
            <h4 className="svc-detail__section-title">
              <i className="bi bi-card-checklist" style={{ marginInlineStart: 6 }} />
              الاشتراكات ({subscriptions.length})
            </h4>
            <button
              className="svc-action svc-action--outline"
              style={{ padding: '6px 14px', fontSize: '0.82rem' }}
              onClick={() => setShowCreateSub(true)}
            >
              <i className="bi bi-plus" />
              إضافة اشتراك
            </button>
          </div>

          {subscriptions.length === 0 ? (
            <p style={{ color: '#6c757d', fontSize: '0.9rem' }}>لا توجد اشتراكات بعد</p>
          ) : (
            subscriptions.map(sub => {
              const balance = sub.effectiveBalance ?? (sub.visitsIncluded - sub.visitsUsed);
              const pct = sub.visitsIncluded > 0 ? Math.max(0, (balance / sub.visitsIncluded) * 100) : 0;

              return (
                <div key={sub._id} className="svc-sub-card">
                  <div className="svc-sub-card__header">
                    <span className="svc-sub-card__location">
                      {sub.locationId?.name || '—'}
                      {sub.locationId?.locationCode && (
                        <span style={{ fontSize: '0.78rem', color: '#6c757d', marginInlineEnd: 8 }}>
                          {sub.locationId.locationCode}
                        </span>
                      )}
                    </span>
                    <div className="svc-sub-card__balance-wrap">
                      <span className={`svc-balance ${balance <= 0 ? 'svc-balance--zero' : balance <= 2 ? 'svc-balance--low' : 'svc-balance--ok'}`}>
                        {balance} / {sub.visitsIncluded}
                      </span>
                      <div className="svc-balance-bar">
                        <div
                          className={`svc-balance-bar__fill ${getBalanceFillClass(balance, sub.visitsIncluded)}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="svc-sub-card__details">
                    <span className="svc-sub-card__detail">
                      <i className="bi bi-calendar-range" />
                      {formatDate(sub.billingCycleStart)} — {formatDate(sub.billingCycleEnd)}
                    </span>
                    <span className="svc-sub-card__detail">
                      <i className="bi bi-cash" />
                      {sub.totalPrice} ر.س (شامل الضريبة)
                    </span>
                    <span className="svc-sub-card__detail">
                      <i className="bi bi-credit-card" />
                      {PAYMENT_STATUS_LABELS[sub.paymentStatus] || sub.paymentStatus}
                    </span>
                    <span className={`svc-type-badge svc-type-badge--${sub.status === 'active' ? 'residential' : 'commercial'}`}>
                      {SUBSCRIPTION_STATUS_LABELS[sub.status] || sub.status}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                    <button
                      className="svc-action svc-action--outline"
                      style={{ padding: '4px 12px', fontSize: '0.78rem' }}
                      onClick={() => { setSelectedSub(sub); setShowEditSub(true); }}
                    >
                      <i className="bi bi-pencil" /> تعديل
                    </button>
                    <button
                      className="svc-action svc-action--outline"
                      style={{ padding: '4px 12px', fontSize: '0.78rem' }}
                      onClick={() => { setSelectedSub(sub); setShowPayment(true); }}
                    >
                      <i className="bi bi-cash" /> تسجيل دفعة
                    </button>
                    <button
                      className="svc-action svc-action--outline"
                      style={{ padding: '4px 12px', fontSize: '0.78rem' }}
                      onClick={() => navigate(`/services/invoices?clientId=${id}`)}
                    >
                      <i className="bi bi-receipt" /> الفواتير
                    </button>
                    {sub.status === 'active' && (
                      <button
                        className="svc-action svc-action--outline"
                        style={{ padding: '4px 12px', fontSize: '0.78rem', color: '#e65100', borderColor: '#e65100' }}
                        onClick={async () => {
                          if (!window.confirm('هل تريد إيقاف هذا الاشتراك مؤقتاً؟')) return;
                          try { await serviceApi.updateSubscription(sub._id, { status: 'paused' }); await fetchData(); }
                          catch (err) { console.error('Error pausing subscription:', err); }
                        }}
                      >
                        <i className="bi bi-pause-circle" /> إيقاف مؤقت
                      </button>
                    )}
                    {sub.status === 'paused' && (
                      <button
                        className="svc-action svc-action--outline"
                        style={{ padding: '4px 12px', fontSize: '0.78rem', color: '#2e7d32', borderColor: '#2e7d32' }}
                        onClick={async () => {
                          try { await serviceApi.updateSubscription(sub._id, { status: 'active' }); await fetchData(); }
                          catch (err) { console.error('Error reactivating subscription:', err); }
                        }}
                      >
                        <i className="bi bi-play-circle" /> إعادة تفعيل
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Requests */}
        <div className="svc-detail__section">
          <div className="svc-detail__section-header">
            <h4 className="svc-detail__section-title">
              <i className="bi bi-list-check" style={{ marginInlineStart: 6 }} />
              سجل الطلبات ({requests.length})
            </h4>
          </div>

          {requests.length === 0 ? (
            <p style={{ color: '#6c757d', fontSize: '0.9rem' }}>لا توجد طلبات بعد</p>
          ) : (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table className="svc-req-table">
                <thead>
                  <tr>
                    <th>التاريخ</th>
                    <th>الموقع</th>
                    <th>الحالة</th>
                    <th>المهمة</th>
                    <th>المصدر</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(req => (
                    <tr key={req._id}>
                      <td>{formatDate(req.requestedDate)}</td>
                      <td>
                        {req.locationId?.name || '—'}
                        {req.locationId?.locationCode && (
                          <span style={{ fontSize: '0.75rem', color: '#6c757d', marginInlineEnd: 6 }}>
                            {req.locationId.locationCode}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`svc-req-status svc-req-status--${req.status}`}>
                          {REQUEST_STATUS_LABELS[req.status] || req.status}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.82rem', color: '#6c757d' }}>
                        {req.taskId?.ticketId || '—'}
                      </td>
                      <td style={{ fontSize: '0.82rem', color: '#6c757d' }}>
                        {req.source === 'client_portal' ? 'العميل' : 'العمليات'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {req.invoiceId && (
                            <button
                              className="svc-action svc-action--outline"
                              style={{ padding: '4px 12px', fontSize: '0.78rem' }}
                              onClick={() => navigate(`/services/invoices/${req.invoiceId}`)}
                              title="عرض الفاتورة"
                            >
                              <i className="bi bi-receipt" /> الفاتورة
                            </button>
                          )}
                          {!['completed', 'cancelled'].includes(req.status) && (
                            <button
                              className="svc-action svc-action--outline"
                              style={{ padding: '4px 12px', fontSize: '0.78rem' }}
                              onClick={() => handleCancelRequest(req._id)}
                            >
                              إلغاء
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Notes */}
        {client.notes && (
          <div className="svc-detail__section">
            <h4 className="svc-detail__section-title">
              <i className="bi bi-sticky" style={{ marginInlineStart: 6 }} />
              ملاحظات
            </h4>
            <p style={{ fontSize: '0.9rem', color: '#495057', background: '#f8f9fa', padding: 14, borderRadius: 8 }}>
              {client.notes}
            </p>
          </div>
        )}
      </div>

      {/* Modals */}
      <EditClientModal
        show={showEditClient}
        onHide={() => setShowEditClient(false)}
        client={client}
        onUpdated={fetchData}
      />

      <CreateLocationModal
        show={showCreateLoc}
        onHide={() => setShowCreateLoc(false)}
        clientId={id}
        onCreated={fetchData}
      />

      <CreateSubscriptionModal
        show={showCreateSub}
        onHide={() => setShowCreateSub(false)}
        clientId={id}
        locations={locations}
        onCreated={fetchData}
      />

      <EditSubscriptionModal
        show={showEditSub}
        onHide={() => { setShowEditSub(false); setSelectedSub(null); }}
        subscription={selectedSub}
        onUpdated={fetchData}
      />

      <RecordPaymentModal
        show={showPayment}
        onHide={() => { setShowPayment(false); setSelectedSub(null); }}
        subscription={selectedSub}
        defaultPaymentDate={paymentDefaults?.paymentDate}
        onRecorded={fetchData}
      />
    </DesktopLayout>
  );
};

export default ServiceClientDetail;
